import { Injectable, TemplateRef } from '@angular/core';
import { ModalComponent } from '@enginuity/core/organisms/modal/modal.component';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/overlay';
import { Observable } from 'rxjs';

interface PrimaryButton {
  label: string;
  active: boolean;
  alert: boolean;
  disabled: boolean;
  loading: boolean;
  continueBtn: boolean;
  IconType: string;
  Type: string;
}

export const CONFIRM_MODAL_CONFIG = {
  modalHeading: 'Confirm action',
  modalDescription: 'Are you sure you want to proceed with this action?',
  topIcon: 'warning',
  modalType: 'saveView',
  showContent: true,
  showIcon: true,
  showBtns: true,
  showInput: true,
  search: false,
  primaryBtn: {
    label: 'Yes, proceed!',
    active: false,
    alert: false,
    disabled: false,
    loading: false,
    continueBtn: true,
    IconType: 'warning',
    Type: 'withoutIcon',
  } as PrimaryButton,
  secondaryBtn: {
    label: 'Cancel',
    backBtn: true,
  },
  windowClass: 'w-full',
  modalDialogClass: 'w-full',
};

export interface ModalConfig extends MatDialogConfig {
  modalHeading?: string;
  modalTitle?: string;
  modalDescription?: string;
  handlers?: any;
  loading?: boolean;
  primaryBtn?: Partial<PrimaryButton>;
}

@Injectable({ providedIn: 'root' })
export class ModalService {
  private ref: MatDialogRef<any> | undefined;

  constructor(private readonly dialog: MatDialog) {}

  openNative<T>(
    component: ComponentType<T> | TemplateRef<any>,
    config?: MatDialogConfig<any>
  ): MatDialogRef<T, any> {
    const cfg = { panelClass: 'mat-mdc-dialog-content', ...config };
    this.ref = this.dialog.open(component, cfg);
    return this.ref;
  }

  open<T>(component: ComponentType<T> | TemplateRef<T>, config?: ModalConfig) {
    this.ref = this.dialog.open<T>(
      component,
      config || {
        data: {
          modalDetail: {},
        },
      }
    );
    this.applyData(this.ref, config);
    this.applyHandlers(this.ref, config);
    return this.ref.afterClosed();
  }

  confirm(config?: ModalConfig): Observable<any> {
    const { modalTitle, modalDescription, modalHeading, loading, primaryBtn, ...params } =
      config || {};
    const settings = {
      ...params,
      panelClass: 'mat-mdc-dialog-content',
      data: {
        modalDetail: {
          ...CONFIRM_MODAL_CONFIG,
          modalTitle,
          modalDescription,
          modalHeading,
          primaryBtn: {
            ...CONFIRM_MODAL_CONFIG.primaryBtn,
            ...primaryBtn,
            loading: loading || false,
            IconType: loading ? 'loader' : primaryBtn?.IconType || '',
            Type: loading ? 'leftIcon' : primaryBtn?.Type || 'withoutIcon',
            disabled: loading || primaryBtn?.disabled || false,
          },
        },
      },
    };

    return new Observable(observer => {
      this.ref = this.dialog.open(ModalComponent, settings);
      this.applyData(this.ref, settings);

      this.ref.afterClosed().subscribe((result: any) => {
        if (result !== undefined) {
          observer.next(result);
        }
        observer.complete();
      });
    });
  }

  setLoading(loading: boolean) {
    if (this.ref?.componentInstance) {
      this.ref.componentInstance.modalDetail.primaryBtn = {
        ...this.ref.componentInstance.modalDetail.primaryBtn,
        loading,
        IconType: loading ? 'loader' : '',
        Type: loading ? 'leftIcon' : 'withoutIcon',
        disabled: loading,
      };
    }
  }

  dismissAll(): void {
    return this.dialog.closeAll();
  }

  close(result?: any): void {
    return this.ref?.close(result);
  }

  private applyData(ref: MatDialogRef<any>, config: MatDialogConfig | undefined) {
    if (config?.data) {
      Object.keys(config.data).forEach(key => {
        return (ref!.componentInstance[key as string] = config.data[key]);
      });
    }
  }

  private applyHandlers(ref: MatDialogRef<any>, config: ModalConfig | undefined) {
    if (config?.handlers) {
      Object.keys(config.handlers).forEach(key => {
        const fn = config?.handlers[key as string];
        return ref!.componentInstance[key as string].subscribe(fn);
      });
    }
  }
}
